<template>
  <div class="page">
    <div class="page-header">
      <h1 v-if="editando">Editar Pedido #{{ pedido.id }}</h1>
      <h1 v-else>Novo Pedido</h1>
      <p class="page-sub">
        {{
          editando
            ? "Revise e salve as alterações"
            : "Preencha os dados e adicione os itens"
        }}
      </p>
    </div>

    <div v-if="falhasCarregamento.length" class="msg-warning">
      Não foi possível carregar {{ falhasCarregamento.join(", ") }}. Recarregue
      a página antes de salvar.
    </div>

    <form class="pedido-form" @submit.prevent="submitPedido">
      <div class="card">
        <div class="card-section-title">Dados do Pedido</div>

        <div class="form-grid">
          <div class="form-group">
            <div class="label-acao">
              <label for="cliente_id">Cliente</label>
              <button
                type="button"
                class="btn-novo-cadastro"
                title="Cadastrar novo cliente"
                @click="abrirNovoCliente"
              >
                + Novo
              </button>
            </div>
            <select
              id="cliente_id"
              v-model="pedido.cliente_id"
              required
              @change="atualizarNomeCliente"
            >
              <option :value="null" disabled>Selecione o cliente</option>
              <option
                v-for="cliente in clientes"
                :key="cliente.id"
                :value="cliente.id"
              >
                {{ cliente.nome }}
              </option>
            </select>
          </div>

          <div class="form-group contato-group">
            <label>Contato do Cliente</label>
            <div class="contato-inputs">
              <select
                v-model="tipoContato"
                class="contato-tipo"
                @change="atualizarMascaraContato"
              >
                <option value="celular">Celular</option>
                <option value="telefone">Telefone Fixo</option>
              </select>
              <input
                id="contato_cliente"
                v-model="pedido.contato_cliente"
                type="text"
                :placeholder="
                  tipoContato === 'celular'
                    ? '(00) 00000-0000'
                    : '(00) 0000-0000'
                "
                maxlength="15"
                class="contato-valor"
                @input="formatarContato"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="data_entrega">Data de Entrega</label>
            <input
              id="data_entrega"
              v-model="pedido.data_entrega"
              type="date"
              required
            />
          </div>

          <div class="form-group">
            <div class="label-acao">
              <label for="colaborador_id">Responsável pelo Pedido</label>
              <button
                type="button"
                class="btn-novo-cadastro"
                title="Cadastrar novo responsável"
                @click="abrirNovoColaborador"
              >
                + Novo
              </button>
            </div>
            <select id="colaborador_id" v-model="pedido.colaborador_id">
              <option :value="null" disabled>Selecione o responsável</option>
              <option
                v-for="colaborador in colaboradores"
                :key="colaborador.id"
                :value="colaborador.id"
              >
                {{ colaborador.nome }} - {{ funcaoLabel[colaborador.funcao] }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="opcao_frete">Opção de Frete</label>
            <select
              id="opcao_frete"
              v-model="freteIdSelecionado"
              @change="freteAlterado = true"
            >
              <option :value="null">— Selecione o frete —</option>
              <option
                v-for="frete in fretesAtivos"
                :key="frete.id"
                :value="frete.id"
              >
                {{ labelFrete(frete) }}
              </option>
            </select>
            <small v-if="freteSalvoSemCorrespondente" class="frete-hint">
              Frete gravado neste pedido: {{ freteSalvoLabel }}. Ele é mantido
              ao salvar, a menos que você escolha outro frete acima.
            </small>
          </div>

          <div v-if="freteValorVariavel" class="form-group">
            <label for="valor_frete_informado">Valor do frete *</label>
            <InputMoeda
              id="valor_frete_informado"
              :model-value="valorFreteInformado"
              @update:model-value="
                valorFreteInformado = $event;
                freteAlterado = true;
              "
            />
            <small class="frete-hint">
              {{ freteSelecionado.cidade || freteSelecionado.tipo_frete }} não
              tem valor fixo: informe o valor cobrado neste pedido.
            </small>
          </div>

          <div class="form-group">
            <label for="local_evento">Local do Evento</label>
            <input
              id="local_evento"
              v-model="pedido.local_evento"
              type="text"
              placeholder="Local onde será o evento"
            />
          </div>

          <div class="form-group">
            <label for="desconto">Desconto (R$)</label>
            <input
              id="desconto"
              v-model.number="pedido.desconto"
              type="number"
              step="0.01"
              min="0"
              inputmode="decimal"
              placeholder="0,00"
            />
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-section-title">Itens do Pedido</div>

        <div
          v-for="(item, index) in pedido.itens"
          :key="item._uid"
          class="item-row"
        >
          <div class="item-row__fields">
            <div class="form-group">
              <label :for="`produto_id_${index}`">Produto</label>
              <select
                :id="`produto_id_${index}`"
                v-model="item.produto_id"
                required
                @change="buscarPrecoVigente(index)"
              >
                <option value="" disabled>Selecione um Produto</option>
                <option
                  v-for="produto in produtos"
                  :key="produto.id"
                  :value="produto.id"
                >
                  {{ produto.nome }} (Mod: {{ produto.modelo }})
                </option>
              </select>
              <span v-if="precosVigentes[item._uid]" class="preco-vigente-hint">
                Preço vigente em
                {{ formatarData(precosVigentes[item._uid].data) }}:
                <strong>{{
                  formatarValor(precosVigentes[item._uid].valor_venda)
                }}</strong>
              </span>
              <span
                v-else-if="precosIndisponiveis[item._uid]"
                class="preco-vigente-hint preco-vigente-hint--alerta"
                >Sem preço cadastrado para esta data.</span
              >
            </div>

            <div class="form-group">
              <label :for="`quantidade_${index}`">Quantidade</label>
              <input
                :id="`quantidade_${index}`"
                v-model.number="item.quantidade_pedida"
                type="number"
                min="1"
                inputmode="numeric"
                required
              />
            </div>

            <div class="form-group">
              <label :for="`cor_${index}`">Cor (se aplicável)</label>
              <select :id="`cor_${index}`" v-model="item.cor_id">
                <option :value="null">— Sem cor —</option>
                <option v-for="cor in cores" :key="cor.id" :value="cor.id">
                  {{ cor.nome }}{{ cor.codigo ? ` (${cor.codigo})` : "" }}
                </option>
              </select>
              <span
                v-if="avisoCor(item)"
                class="preco-vigente-hint preco-vigente-hint--alerta"
                >⚠ {{ avisoCor(item) }}</span
              >
            </div>
          </div>

          <button
            type="button"
            class="btn btn-sm item-row__remove"
            aria-label="Remover item"
            @click="removerItem(index)"
          >
            <AppIcon
              name="fechar"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="14"
              height="14"
            />
            Remover
          </button>
        </div>

        <button
          type="button"
          class="btn btn-secondary btn-sm add-item-btn"
          @click="adicionarItem"
        >
          + Adicionar Item
        </button>
      </div>
    </form>

    <!-- Perto do botão: quem clica em salvar no fim da página vê o erro -->
    <div v-if="errorMessage" class="msg-error">{{ errorMessage }}</div>
    <div v-if="warningMessage" class="msg-warning">{{ warningMessage }}</div>

    <!-- Cadastro rápido sem sair do pedido: o novo registro já fica
         selecionado -->
    <AppModal
      v-if="novoCliente"
      titulo="Novo Cliente"
      formulario
      :erro="erroNovoCadastro"
      @fechar="novoCliente = null"
      @enviar="salvarNovoCliente"
    >
      <div class="form-group">
        <label for="novo_cliente_nome">Nome *</label>
        <input
          id="novo_cliente_nome"
          v-model="novoCliente.nome"
          type="text"
          placeholder="Nome completo"
          required
        />
      </div>
      <div class="form-group">
        <label for="novo_cliente_telefone">Telefone</label>
        <input
          id="novo_cliente_telefone"
          v-model="novoCliente.telefone"
          type="tel"
          placeholder="(00) 00000-0000"
        />
      </div>
      <div class="form-group">
        <label for="novo_cliente_instagram">Instagram</label>
        <input
          id="novo_cliente_instagram"
          v-model="novoCliente.instagram"
          type="text"
          placeholder="@usuario"
        />
      </div>
      <template #rodape>
        <button
          type="button"
          class="btn btn-secondary"
          @click="novoCliente = null"
        >
          Cancelar
        </button>
        <button type="submit" class="btn btn-primary" :disabled="salvandoNovo">
          {{ salvandoNovo ? "Salvando…" : "Cadastrar cliente" }}
        </button>
      </template>
    </AppModal>

    <AppModal
      v-if="novoColaborador"
      titulo="Novo Responsável"
      formulario
      :erro="erroNovoCadastro"
      @fechar="novoColaborador = null"
      @enviar="salvarNovoColaborador"
    >
      <div class="form-group">
        <label for="novo_colaborador_nome">Nome *</label>
        <input
          id="novo_colaborador_nome"
          v-model="novoColaborador.nome"
          type="text"
          placeholder="Nome do responsável"
          required
        />
      </div>
      <div class="form-group">
        <label for="novo_colaborador_funcao">Função</label>
        <select id="novo_colaborador_funcao" v-model="novoColaborador.funcao">
          <option value="doceira">Doceira</option>
          <option value="organizador">Organizador de Eventos</option>
          <option value="promoter">Promoter</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      <div class="form-group">
        <label for="novo_colaborador_telefone">Telefone</label>
        <input
          id="novo_colaborador_telefone"
          v-model="novoColaborador.telefone"
          type="tel"
          placeholder="(00) 00000-0000"
        />
      </div>
      <template #rodape>
        <button
          type="button"
          class="btn btn-secondary"
          @click="novoColaborador = null"
        >
          Cancelar
        </button>
        <button type="submit" class="btn btn-primary" :disabled="salvandoNovo">
          {{ salvandoNovo ? "Salvando…" : "Cadastrar responsável" }}
        </button>
      </template>
    </AppModal>

    <div class="form-footer-actions">
      <button type="button" class="btn btn-secondary" @click="$router.back()">
        Cancelar
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="salvando"
        @click="submitPedido"
      >
        {{
          salvando
            ? "Salvando…"
            : editando
            ? "Salvar Alterações"
            : "Criar Pedido"
        }}
      </button>
    </div>
  </div>
</template>

<script>
// Tela única de Novo e Editar Pedido (rotas /pedidos/novo e
// /pedidos/:id/editar). Antes eram duas telas copiadas que já divergiam.
import pedidosService from "@/services/pedidos";
import produtosService from "@/services/produtos";
import clientesService from "@/services/clientes";
import colaboradoresService from "@/services/colaboradores";
import coresService from "@/services/cores";
import fretesService from "@/services/fretes";
import precoProdutoService from "@/services/precoProduto";
import { formatCurrency, formatDate, localISODate } from "@/utils/formatters";
import InputMoeda from "@/components/InputMoeda.vue";
import AppModal from "@/components/AppModal.vue";
import { mensagemErro } from "@/utils/erros";

let proximoUid = 1;
const itemVazio = () => ({
  _uid: proximoUid++,
  produto_id: null,
  quantidade_pedida: 1,
  cor_id: null,
});

export default {
  name: "PedidoFormView",
  components: { InputMoeda, AppModal },
  data() {
    return {
      falhasCarregamento: [],
      salvando: false,
      pedido: {
        nome_cliente: "",
        contato_cliente: "",
        data_entrega: "",
        nome_doceira: "",
        opcao_frete: "Retirada",
        local_evento: "",
        desconto: 0,
        status: "pendente",
        itens: [itemVazio()],
      },
      tipoContato: "celular",
      clientes: [],
      colaboradores: [],
      produtos: [],
      cores: [],
      fretes: [],
      freteIdSelecionado: null,
      // Na edição, só envia frete ao salvar se a pessoa trocou no select:
      // antes o frete era recalculado pela tabela atual (valor novo, ou 0 se
      // o frete tinha sido desativado), reescrevendo o valor do pedido.
      freteAlterado: false,
      // Valor digitado quando o frete não tem valor fixo (Correios,
      // transportadora); na edição, vem com o valor gravado no pedido.
      valorFreteInformado: null,
      errorMessage: "",
      warningMessage: "",
      precosVigentes: {},
      precosIndisponiveis: {},
      // cadastro rápido de cliente/responsável (null = janela fechada)
      novoCliente: null,
      novoColaborador: null,
      salvandoNovo: false,
      erroNovoCadastro: "",
      funcaoLabel: {
        doceira: "Doceira",
        organizador: "Organizador",
        promoter: "Promoter",
        outro: "Outro",
      },
    };
  },
  computed: {
    editando() {
      return !!this.$route.params.id;
    },
    fretesAtivos() {
      return this.fretes.filter((f) => f.ativo);
    },
    freteSelecionado() {
      return this.fretes.find((f) => f.id === this.freteIdSelecionado) || null;
    },
    freteValorVariavel() {
      return !!this.freteSelecionado?.valor_variavel;
    },
    freteSalvoSemCorrespondente() {
      return (
        this.editando &&
        !this.freteAlterado &&
        !!this.pedido.id &&
        !!this.pedido.opcao_frete &&
        this.freteIdSelecionado === null
      );
    },
    freteSalvoLabel() {
      const valor = Number(this.pedido.valor_frete ?? 0);
      return valor > 0
        ? `${this.pedido.opcao_frete} — ${formatCurrency(valor)}`
        : this.pedido.opcao_frete;
    },
  },
  watch: {
    "pedido.data_entrega"() {
      this.pedido.itens.forEach((_, index) => this.buscarPrecoVigente(index));
    },
  },
  async created() {
    await Promise.all([
      this.fetchProdutos(),
      this.fetchCores(),
      this.fetchClientes(),
      this.fetchColaboradores(),
      this.fetchFretes(),
    ]);
    // O pedido vem depois das listas: cor, cliente e frete são casados com elas
    if (this.editando) await this.fetchPedido();
  },
  methods: {
    registrarFalha(nome) {
      if (!this.falhasCarregamento.includes(nome))
        this.falhasCarregamento.push(nome);
    },
    async fetchPedido() {
      try {
        const response = await pedidosService.getById(this.$route.params.id);
        const pedido = response.data;
        pedido.itens = (pedido.pedido_itens || []).map((item) => {
          let cor_id = item.cor_id != null ? Number(item.cor_id) : null;
          if (!cor_id && (item.nome_cor || item.cor_selecionada)) {
            const nome = (item.nome_cor || item.cor_selecionada || "")
              .trim()
              .toLowerCase();
            const match = this.cores.find(
              (c) => (c.nome || "").toLowerCase() === nome
            );
            if (match) cor_id = match.id;
          }
          return {
            ...item,
            cor_id,
            // Ligado aqui pelo nome (o item estava só com o texto da cor)
            _corLigadaPeloNome: item.cor_id == null && cor_id != null,
            _corIdOriginal: item.cor_id != null ? Number(item.cor_id) : null,
            _produtoIdOriginal: item.produto_id,
            _uid: proximoUid++,
          };
        });
        if (pedido.data_entrega) {
          pedido.data_entrega = pedido.data_entrega.split("T")[0];
        }
        // Recupera cliente_id pelo nome caso não venha preenchido do backend
        if (!pedido.cliente_id && pedido.nome_cliente) {
          const match = this.clientes.find(
            (c) =>
              c.nome.trim().toLowerCase() ===
              pedido.nome_cliente.trim().toLowerCase()
          );
          if (match) pedido.cliente_id = match.id;
        }
        // Contato vazio: usa o telefone atual do cliente
        if (!pedido.contato_cliente && pedido.cliente_id) {
          const cliente = this.clientes.find(
            (c) => c.id === Number(pedido.cliente_id)
          );
          if (cliente?.telefone) pedido.contato_cliente = cliente.telefone;
        }
        // Identifica o frete pelo nome + valor_frete salvo no pedido
        if (pedido.opcao_frete) {
          const valorSalvo = Number(
            pedido.valor_frete ?? pedido.valor_frete_calculado ?? 0
          );
          const mesmoNome = (f) =>
            f.ativo &&
            (f.cidade === pedido.opcao_frete ||
              f.tipo_frete === pedido.opcao_frete);
          // Frete de valor variável casa só pelo nome; o valor é o do pedido
          const freteExato = this.fretes.find(
            (f) =>
              mesmoNome(f) &&
              (f.valor_variavel ||
                Math.abs(Number(f.valor) - valorSalvo) < 0.01)
          );
          if (freteExato?.valor_variavel) this.valorFreteInformado = valorSalvo;
          // Sem frete ativo com o mesmo valor: não pré-seleciona outro (o
          // valor gravado é mantido e aparece como aviso abaixo do select).
          this.freteIdSelecionado = freteExato ? freteExato.id : null;
        }
        this.pedido = pedido;
      } catch (error) {
        this.errorMessage =
          "Erro ao carregar pedido: " +
          (error.response?.data?.error || error.message);
      }
    },
    async fetchProdutos() {
      try {
        const response = await produtosService.getAll();
        this.produtos = [...response.data].sort((a, b) =>
          (a.nome || "").localeCompare(b.nome || "", "pt-BR")
        );
      } catch {
        this.registrarFalha("os produtos");
      }
    },
    async fetchCores() {
      try {
        const response = await coresService.listar();
        this.cores = response.data;
      } catch {
        this.registrarFalha("as cores");
      }
    },
    async fetchClientes() {
      try {
        const response = await clientesService.getAll();
        this.clientes = [...response.data].sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR")
        );
      } catch {
        this.registrarFalha("os clientes");
      }
    },
    abrirNovoCliente() {
      this.erroNovoCadastro = "";
      this.novoCliente = { nome: "", telefone: "", instagram: "", ativo: true };
    },
    abrirNovoColaborador() {
      this.erroNovoCadastro = "";
      this.novoColaborador = {
        nome: "",
        funcao: "doceira",
        telefone: "",
        ativo: true,
      };
    },
    async salvarNovoCliente() {
      if (this.salvandoNovo || !this.novoCliente.nome.trim()) return;
      this.salvandoNovo = true;
      this.erroNovoCadastro = "";
      try {
        const { data } = await clientesService.create(this.novoCliente);
        this.clientes = [...this.clientes, data].sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR")
        );
        this.pedido.cliente_id = data.id;
        this.atualizarNomeCliente();
        this.novoCliente = null;
      } catch (error) {
        this.erroNovoCadastro = mensagemErro(error, "Erro ao salvar cliente.");
      } finally {
        this.salvandoNovo = false;
      }
    },
    async salvarNovoColaborador() {
      if (this.salvandoNovo || !this.novoColaborador.nome.trim()) return;
      this.salvandoNovo = true;
      this.erroNovoCadastro = "";
      try {
        const { data } = await colaboradoresService.create(
          this.novoColaborador
        );
        this.colaboradores = [...this.colaboradores, data].sort((a, b) =>
          (a.nome || "").localeCompare(b.nome || "", "pt-BR")
        );
        this.pedido.colaborador_id = data.id;
        this.novoColaborador = null;
      } catch (error) {
        this.erroNovoCadastro = mensagemErro(
          error,
          "Erro ao salvar responsável."
        );
      } finally {
        this.salvandoNovo = false;
      }
    },
    async fetchColaboradores() {
      try {
        const response = await colaboradoresService.getAll();
        this.colaboradores = [...response.data].sort((a, b) =>
          (a.nome || "").localeCompare(b.nome || "", "pt-BR")
        );
      } catch {
        this.registrarFalha("os responsáveis");
      }
    },
    async fetchFretes() {
      try {
        const response = await fretesService.getAll();
        this.fretes = response.data;
      } catch {
        this.registrarFalha("os fretes");
      }
    },
    labelFrete(frete) {
      const destino = frete.cidade || frete.tipo_frete;
      if (frete.valor_variavel) return `${destino} — valor informado no pedido`;
      const valor = Number(frete.valor);
      if (valor > 0) {
        return `${destino} — R$ ${valor.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
        })}`;
      }
      return destino;
    },
    adicionarItem() {
      this.pedido.itens.push(itemVazio());
    },
    atualizarNomeCliente() {
      const cliente = this.clientes.find(
        (c) => c.id === Number(this.pedido.cliente_id)
      );
      if (cliente) {
        this.pedido.nome_cliente = cliente.nome;
        if (cliente.telefone) {
          const digits = cliente.telefone.replace(/\D/g, "");
          this.tipoContato = digits.length === 11 ? "celular" : "telefone";
          this.pedido.contato_cliente = cliente.telefone;
        }
      }
    },
    atualizarMascaraContato() {
      this.pedido.contato_cliente = "";
    },
    formatarContato(event) {
      let valor = event.target.value.replace(/\D/g, "");
      const max = this.tipoContato === "celular" ? 11 : 10;
      if (valor.length > max) valor = valor.substring(0, max);
      if (valor.length <= 2) {
        valor = valor.length > 0 ? `(${valor}` : "";
      } else if (valor.length <= 6) {
        valor = `(${valor.substring(0, 2)}) ${valor.substring(2)}`;
      } else if (this.tipoContato === "celular" && valor.length <= 11) {
        valor = `(${valor.substring(0, 2)}) ${valor.substring(
          2,
          7
        )}-${valor.substring(7)}`;
      } else if (this.tipoContato === "telefone" && valor.length <= 10) {
        valor = `(${valor.substring(0, 2)}) ${valor.substring(
          2,
          6
        )}-${valor.substring(6)}`;
      }
      this.pedido.contato_cliente = valor;
    },
    // Aviso quando a cor gravada no item não bate com o cadastro de cores
    avisoCor(item) {
      if (!item.id || !item.cor_selecionada) return "";
      const atual = this.cores.find((c) => c.id === item.cor_id);
      if (item._corLigadaPeloNome && atual)
        return `Gravado só como "${item.cor_selecionada}"; ligado pelo nome à cor cadastrada "${atual.nome}". Confira antes de salvar.`;
      if (item.cor_sem_cadastro && item.cor_id == null)
        return `Gravado como "${item.cor_selecionada}", que não está no cadastro de cores. Escolha a cor cadastrada.`;
      if (
        item.cor_texto_divergente &&
        atual &&
        item.cor_id === item._corIdOriginal
      )
        return `Gravado como "${item.cor_selecionada}"; no cadastro a cor é "${atual.nome}".`;
      return "";
    },
    removerItem(index) {
      const uid = this.pedido.itens[index]?._uid;
      this.pedido.itens.splice(index, 1);
      this.limparPreco(uid);
    },
    limparPreco(uid) {
      const v = { ...this.precosVigentes };
      const i = { ...this.precosIndisponiveis };
      delete v[uid];
      delete i[uid];
      this.precosVigentes = v;
      this.precosIndisponiveis = i;
    },
    // Preço que o item vai receber: item novo, ou item gravado que trocou de
    // produto (o backend usa o preço vigente na data de entrega). Item
    // gravado com o mesmo produto mantém o preço dele: sem dica.
    async buscarPrecoVigente(index) {
      const item = this.pedido.itens[index];
      if (!item) return;
      this.limparPreco(item._uid);
      if (!item.produto_id) return;
      if (item.id && item.produto_id === item._produtoIdOriginal) return;
      const data = this.pedido.data_entrega || localISODate();
      try {
        const res = await precoProdutoService.getVigente(item.produto_id, data);
        this.precosVigentes = {
          ...this.precosVigentes,
          [item._uid]: { valor_venda: res.data.valor_venda, data },
        };
      } catch {
        this.precosIndisponiveis = {
          ...this.precosIndisponiveis,
          [item._uid]: true,
        };
      }
    },
    formatarData: formatDate,
    formatarValor: formatCurrency,
    validar() {
      const faltando = [];
      if (!this.pedido.nome_cliente) faltando.push("Nome do cliente");
      if (!this.pedido.data_entrega) faltando.push("Data de Entrega");
      if (faltando.length > 0)
        return `Campos obrigatórios não preenchidos: ${faltando.join(", ")}.`;
      if (this.pedido.itens.length === 0)
        return "Adicione pelo menos um item ao pedido.";
      const itemInvalido = this.pedido.itens.some(
        (item) =>
          !item.produto_id ||
          !item.quantidade_pedida ||
          item.quantidade_pedida <= 0
      );
      if (itemInvalido)
        return "Verifique os itens: produto e quantidade são obrigatórios e a quantidade deve ser maior que zero.";
      if (this.freteValorVariavel && !(this.valorFreteInformado > 0))
        return "Informe o valor do frete.";
      return "";
    },
    // opcao_frete + valor_frete do frete escolhido no select
    dadosFrete() {
      const frete = this.freteSelecionado;
      return {
        opcao_frete: frete
          ? frete.cidade || frete.tipo_frete
          : this.pedido.opcao_frete,
        valor_frete: !frete
          ? 0
          : frete.valor_variavel
          ? this.valorFreteInformado
          : Number(frete.valor),
      };
    },
    payloadNovo() {
      const payload = { ...this.pedido, ...this.dadosFrete() };
      delete payload.valor_pago;
      payload.status = "pendente";
      return payload;
    },
    payloadEdicao() {
      const payload = {
        cliente_id: this.pedido.cliente_id,
        nome_cliente: this.pedido.nome_cliente,
        contato_cliente: this.pedido.contato_cliente,
        data_entrega: this.pedido.data_entrega,
        nome_doceira: this.pedido.nome_doceira,
        local_evento: this.pedido.local_evento,
        desconto: this.pedido.desconto,
        colaborador_id: this.pedido.colaborador_id,
        status: this.pedido.status,
        itens: (this.pedido.itens || []).map((item) => {
          const limpo = {
            id: item.id || undefined,
            produto_id: item.produto_id ? Number(item.produto_id) : undefined,
            quantidade_pedida: item.quantidade_pedida
              ? Number(item.quantidade_pedida)
              : undefined,
            cor_id: item.cor_id ? Number(item.cor_id) : null,
          };
          Object.keys(limpo).forEach((key) => {
            if (limpo[key] === undefined) delete limpo[key];
          });
          return limpo;
        }),
      };
      if (this.freteAlterado) Object.assign(payload, this.dadosFrete());
      return payload;
    },
    async submitPedido() {
      if (this.salvando) return;
      this.errorMessage = "";
      this.warningMessage = "";

      if (this.editando && !this.pedido.id) {
        this.errorMessage =
          "O pedido não foi carregado. Recarregue a página antes de salvar.";
        return;
      }
      const erro = this.validar();
      if (erro) {
        this.errorMessage = erro;
        return;
      }

      this.salvando = true;
      try {
        if (this.editando) {
          const id = this.$route.params.id;
          await pedidosService.update(id, this.payloadEdicao());
          this.$router.push(`/pedidos/${id}`);
        } else {
          const response = await pedidosService.create(this.payloadNovo());
          this.$router.push(`/pedidos/${response.data.id}`);
        }
        // Continua "salvando" até sair da tela: evita gravar duas vezes
      } catch (error) {
        this.salvando = false;
        const acao = this.editando ? "atualizar" : "criar";
        this.errorMessage = error.response?.data?.error
          ? `Erro ao ${acao} pedido: ${error.response.data.error}`
          : `Ocorreu um erro ao ${acao} o pedido. Verifique os dados e tente novamente.`;
      }
    },
  },
};
</script>

<style scoped>
.label-acao {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.btn-novo-cadastro {
  padding: 0;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--c-accent-dark);
  cursor: pointer;
  white-space: nowrap;
}

.btn-novo-cadastro:hover {
  text-decoration: underline;
}

.frete-hint {
  display: block;
  margin-top: 0.375rem;
  font-size: 0.8rem;
  color: var(--c-text-muted);
}

.pedido-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ── Card section title ─── */
.card-section-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--c-text-muted);
  padding: 0.875rem 1.5rem;
  border-bottom: 1px solid var(--c-border-light);
  background: linear-gradient(
    to bottom,
    var(--c-surface-warm),
    var(--c-surface)
  );
}

/* ── Form grid ─── */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  padding: 1.5rem;
}

/* ── Contato com select + input lado a lado ─── */
.contato-group {
  grid-column: span 2;
}

.contato-inputs {
  display: flex;
  gap: 0.5rem;
}

.contato-tipo {
  width: 140px;
  flex-shrink: 0;
}

.contato-valor {
  flex: 1;
}

/* ── Item de pedido ─── */
.item-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--c-border-light);
}

.item-row:last-of-type {
  border-bottom: none;
}

.item-row__fields {
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
}

.item-row__remove {
  margin-top: 1.625rem;
  flex-shrink: 0;
  color: var(--c-danger);
  border-color: var(--c-danger-border);
  background: var(--c-danger-bg);
}

.item-row__remove:hover {
  background: var(--c-danger-border);
}

.preco-vigente-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--c-text-muted);
}

.preco-vigente-hint--alerta {
  color: var(--c-warn, #b45309);
}

.add-item-btn {
  margin: 1rem 1.5rem;
}

/* ── Page sub ─── */
.page-sub {
  font-size: 0.9375rem;
  color: var(--c-text-muted);
  margin-top: 0.25rem;
}

.form-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--c-border-light);
}

/* ── Responsivo ─── */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
    gap: 0;
  }

  .contato-group {
    grid-column: span 1;
  }

  .item-row__fields {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .item-row {
    flex-direction: column;
    padding: 1rem;
    gap: 0;
  }

  .item-row__fields {
    width: 100%;
    grid-template-columns: 1fr;
    gap: 0;
  }

  .item-row__remove {
    margin-top: 0.5rem;
    width: 100%;
    justify-content: center;
  }

  .contato-inputs {
    flex-direction: column;
  }

  .contato-tipo {
    width: 100%;
  }

  .card-section-title {
    padding: 0.75rem 1rem;
  }

  .add-item-btn {
    margin: 0.75rem 1rem;
    width: calc(100% - 2rem);
    justify-content: center;
  }

  .form-footer-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }

  .form-footer-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
